import { computed, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, startWith } from 'rxjs';
import { IVdoSidebarConfig } from '../interfaces/vdo-sidebar-config.interface';
import { IVdoMenuItem } from '../interfaces/vdo-menu-item.interface';
import { VDO_MENU, VDO_PUBLIC_MENU, MENU_IDS } from '../constants/vdo-menu.const';
import { NavigationEnd, Router } from '@angular/router';
import { PolicyService } from '@core/services/policy.service';
import { VdoSidebarHelper } from '../helpers/vdo-sidebar.helper';

@Injectable({
  providedIn: 'root',
})
export class VdoSidebarService {
  private readonly policyService = inject(PolicyService);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(true);
  isOpen$ = this.isOpen$$.asObservable();

  sidenavConfig$: Observable<IVdoSidebarConfig> = combineLatest([this.isOpen$]).pipe(
    map(([isOpen]) => ({
      isOpen,
      matSidenavConfig: {
        open: true,
        mode: 'side',
        hasBackdrop: false,
      },
      appSidebarConfig: {
        compact: !isOpen,
      },
    }))
  );

  private readonly router = inject(Router);
  private readonly isPublicMenu = signal(false);

  private readonly allowedMenuIds = computed<string[] | 'all'>(() => {
    const isPolicyAccepted = this.policyService.isPolicyAccepted();

    if (isPolicyAccepted) {
      return 'all';
    }

    return [MENU_IDS.MAIN.SETTINGS, MENU_IDS.SETTINGS.TERMS_OF_USE];
  });

  readonly menuItems = computed<IVdoMenuItem[]>(() => {
    if (this.isPublicMenu()) {
      return VDO_PUBLIC_MENU;
    }

    const baseMenu = VdoSidebarHelper.deriveMenuBasedOnAllowedIds(VDO_MENU, this.allowedMenuIds());
    return VdoSidebarHelper.filterMenuByAllowed(baseMenu);
  });

  constructor() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => {
        const url = this.router.url;
        this.isPublicMenu.set(url.startsWith('/publico'));
      });
  }

  open() {
    this.isOpen$$.next(true);
  }

  close() {
    this.isOpen$$.next(false);
  }

  toggle() {
    if (this.isOpen$$.value) {
      this.close();
    } else {
      this.open();
    }
  }
}
